import React, {
  ComponentProps,
  ComponentType,
  Dispatch,
  MouseEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";

import { ContextMenuItem } from "veneer/ContextMenu";
import DataTable, { AppliedFilter, BulkActionBarItem } from "veneer/DataTable";
import Stack from "veneer/Stack";

import { strategiesSelector } from "cortex/ui/src/fleet/redux-store/strategies/selectors";
import { upgradablePlayerVersionsSelector } from "cortex/ui/src/fleet/redux-store/ui-state/player-versions/selectors";
import { useAppSelector, useFeatureFlag } from "cortex/ui/src/hooks";
import { NavState } from "cortex/ui/src/navigation/model";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import { deviceWithStatusSelector } from "cortex/ui/src/redux-store/derivativeSelectors";
import {
  hasPendingUpdate,
  isKmpPlayer,
} from "cortex/ui/src/redux-store/devices/selectors";
import {
  LastSeenDeviceCounts,
  devicesLastSeenSelector,
} from "cortex/ui/src/redux-store/last-seen-devices/selectors";
import { networksSelector } from "cortex/ui/src/redux-store/networks/selectors";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import { isAdmin, isReadonly } from "cortex/ui/src/redux-store/users/selectors";
import ButtonGroup from "cortex/ui/src/ui/button-group";
import DeviceStats from "cortex/ui/src/ui/device-stats/DeviceStats";
import { LoadingIndicator } from "cortex/ui/src/ui/loader";
import { exactOrPartialFilter } from "cortex/ui/src/ui/table/search";
import { Option } from "cortex/ui/src/ui/table/types";
import { usePersistedTableState } from "cortex/ui/src/ui/table/usePersistedTableState";
import { Panel } from "cortex/ui/src/view/layout";

import CancelPendingDrawer from "./bulk-actions/CancelPendingDrawer";
import DeleteDeviceDrawer from "./bulk-actions/DeleteDeviceDrawer";
import RegisterBulkDevicesDrawer from "./bulk-actions/RegisterBulkDevicesDrawer";
import RegisterSingleDeviceDrawer from "./bulk-actions/RegisterSingleDeviceDrawer";
import RestartDeviceDrawer from "./bulk-actions/RestartDeviceDrawer";
import SystemStrategyDrawer from "./bulk-actions/SystemStrategyDrawer";
import UpDowngradePlayerDrawer from "./bulk-actions/UpDowngradePlayerDrawer";
import UpgradeAgentDrawer from "./bulk-actions/UpgradeAgentDrawer";
import UpgradeFirmwareDrawer from "./bulk-actions/UpgradeFirmwareDrawer";
import {
  BULK_ACTIONS,
  BulkAction,
  DeviceTableActionsProvider,
} from "./DeviceTableActionsContext";
import { getColumns } from "./DeviceTableColumns";
import DeviceTableFilters from "./DeviceTableFilters";
import {
  Device,
  DeviceFilterId,
  DeviceMetricFilter,
  DeviceNetworkTypeFilter,
  FilterState,
  LastConnectedFilter,
  LastConnectedTier,
} from "../model";
import {
  DEVICE_TABLE_DEFAULT_VISIBLE_COLUMNS,
  asIdStr,
  calculateVisibleDeviceMetricsCounts,
  calculateVisibleLastSeenDeviceCounts,
  exportSelectedDevices,
  filterDevices,
  getAvailableDeviceFilters,
  getAvailableNetworkOptions,
  getAvailableOsOptions,
  getAvailableVisibleDeviceFilters,
  getDeviceMetricFilters,
  getInitialDeviceFilters,
  getLastConnectedFilters,
  getSelectedFilterOptions,
  getSelectedFilterValues,
  getVisibleDeviceFilters,
  isSupportedUpdateOs,
  removeValue,
} from "../utils";

const getAllDevicesAsArray = makeGetAllAsArray<Device>();
const INITIAL_PAGE_SIZE = 10 as const;

type BulkActionBaseProps = {
  networkOptions?: ReadonlyArray<Option> | undefined;
  onCancel: () => void;
  open: boolean;
  selectedItems: ReadonlyArray<Device>;
  setSelectedItems: (items: ReadonlyArray<Device>) => void;
  tagOptions?: ReadonlyArray<Option>;
};

const BULK_ACTION_COMPONENTS: Partial<
  Record<BulkAction, ComponentType<BulkActionBaseProps>>
> = {
  [BULK_ACTIONS.CANCEL_PENDING_UPGRADE]: CancelPendingDrawer,
  [BULK_ACTIONS.DELETE]: DeleteDeviceDrawer,
  [BULK_ACTIONS.RESTART]: RestartDeviceDrawer,
  [BULK_ACTIONS.STRATEGY]: SystemStrategyDrawer,
  [BULK_ACTIONS.UP_DOWNGRADE_PLAYER]: UpDowngradePlayerDrawer,
  [BULK_ACTIONS.UPGRADE_AGENT]: UpgradeAgentDrawer,
  [BULK_ACTIONS.UPGRADE_FIRMWARE]: UpgradeFirmwareDrawer,
};

type SelectionHasDeviceCount = {
  agent: number;
  cancelPending: number;
  firmwareUpdate: number;
  player: number;
};

interface DeviceTableProps {
  closeDrawer: () => void;
  deviceStatsCounts?: LastSeenDeviceCounts;
  deviceMetricsCounts?: ComponentProps<
    typeof DeviceStats
  >["deviceMetricsCounts"];
  networkFilter?: boolean;
  networkOptions?: ReadonlyArray<Option>;
  onRowClick: (row: Device, isSelected: boolean) => void;
  prefiltered?: { network?: number };
  storageKey?: string;
  storesLoaded?: boolean;
  tagsOptions?: ReadonlyArray<Option>;
  venueSyncEnabled?: boolean;
  versionOptions?: ReadonlyArray<Option>;
}

const filter = exactOrPartialFilter<Device>([
  "device_id",
  "name",
  "network_name",
  "strategy_name",
  "tags",
]);

function makeSelectedTagOptions(
  ids: ReadonlyArray<string> | null | undefined,
  options?: ReadonlyArray<Option>,
): ReadonlyArray<Option> {
  const list = ids ?? [];
  return list.map((id) => {
    const match = options?.find((o) => asIdStr(o.value) === asIdStr(id));
    return match ?? { label: asIdStr(id), value: asIdStr(id) };
  });
}

function getAppliedLastConnectedFilters(
  filters: ReadonlyArray<LastConnectedFilter>,
): ReadonlyArray<LastConnectedFilter> {
  const hasActive = filters.includes("Connected") || filters.includes("Active");
  const hasHibernating =
    filters.includes("Connected") || filters.includes("Hibernating");
  const otherFilters = filters.filter(
    (filter) =>
      filter !== "Connected" && filter !== "Active" && filter !== "Hibernating",
  );

  if (hasActive && hasHibernating) {
    return ["Connected", ...otherFilters];
  }

  if (hasActive) {
    return ["Active", ...otherFilters];
  }

  if (hasHibernating) {
    return ["Hibernating", ...otherFilters];
  }

  return otherFilters;
}

export default function DeviceTable({
  closeDrawer,
  deviceMetricsCounts,
  deviceStatsCounts,
  prefiltered = {},
  networkFilter = true,
  networkOptions = [],
  onRowClick,
  storageKey = "cortex-devices",
  storesLoaded,
  tagsOptions = [],
  versionOptions,
}: DeviceTableProps) {
  const location = useLocation<NavState>();
  const { state } = location;
  const history = useHistory();
  const isStrategiesEnabled = useFeatureFlag("is-strategies-enabled");
  const initialQuery = state && state?.q ? state?.q : "";
  const initialVersion =
    state && state?.current_version ? state?.current_version : null;
  const intitialTag = state && state?.tag ? state?.tag : null;
  const initialLastConnected = state?.lastConnected ?? null;
  const initialMetric = state?.metric ?? null;
  const initialNetworkType = state?.networkType ?? null;

  const defaultFilters = useMemo(
    () =>
      getInitialDeviceFilters({
        initialLastConnected,
        initialMetric: null,
        initialNetworkType: null,
        initialTag: intitialTag,
        initialVersion,
        prefilteredNetwork: prefiltered.network,
      }),
    [initialLastConnected, initialVersion, intitialTag, prefiltered.network],
  );

  const initialFilters = useMemo(
    () =>
      getInitialDeviceFilters({
        initialLastConnected,
        initialMetric,
        initialNetworkType,
        initialTag: intitialTag,
        initialVersion,
        prefilteredNetwork: prefiltered.network,
      }),
    [
      initialLastConnected,
      initialMetric,
      initialNetworkType,
      initialVersion,
      intitialTag,
      prefiltered.network,
    ],
  );

  const hasNavigationState =
    state != null &&
    (Boolean(initialQuery) ||
      Boolean(initialVersion) ||
      Boolean(intitialTag) ||
      Boolean(getLastConnectedFilters(initialLastConnected).length) ||
      Boolean(initialMetric) ||
      Boolean(initialNetworkType));

  const {
    filters: persistedFilters,
    setFilters: setPersistedFilters,
    search,
    setSearch,
  } = usePersistedTableState<FilterState>(
    {
      createDefaultFilters: () => defaultFilters,
      storageKey,
    },
    {
      initialQuery,
    },
  );

  const filters = useMemo(() => {
    if (prefiltered.network === undefined) {
      return persistedFilters;
    }

    return {
      ...persistedFilters,
      network: String(prefiltered.network),
    };
  }, [persistedFilters, prefiltered.network]);

  const setFilters = useCallback<Dispatch<SetStateAction<FilterState>>>(
    (update) => {
      setPersistedFilters((previousFilters) => {
        const nextFilters =
          typeof update === "function" ? update(previousFilters) : update;

        if (prefiltered.network === undefined) {
          return nextFilters;
        }

        return {
          ...nextFilters,
          network: String(prefiltered.network),
        };
      });
    },
    [prefiltered.network, setPersistedFilters],
  );

  useEffect(() => {
    if (prefiltered.network === undefined) {
      return;
    }

    setFilters((previousFilters) => previousFilters);
  }, [prefiltered.network, setFilters]);

  useEffect(() => {
    if (!hasNavigationState) {
      return;
    }
    closeDrawer();
    setFilters((prev) => {
      const nextFilters = { ...prev };

      if (initialVersion) {
        nextFilters.version = initialFilters.version;
      }
      if (intitialTag) {
        nextFilters.tags = initialFilters.tags;
      }
      if (getLastConnectedFilters(initialLastConnected).length > 0) {
        nextFilters.lastConnected = initialFilters.lastConnected;
      }
      if (initialMetric) {
        nextFilters.metric = initialFilters.metric;
      }
      if (initialNetworkType) {
        nextFilters.networkType = initialFilters.networkType;
      }

      nextFilters.visibleFilters = Array.from(
        new Set([
          ...getVisibleDeviceFilters(prev),
          ...getVisibleDeviceFilters(initialFilters),
        ]),
      );

      return nextFilters;
    });
    if (initialQuery) {
      setSearch(initialQuery);
    }
    history.replace(`${location.pathname}${location.search}${location.hash}`);
  }, [
    closeDrawer,
    hasNavigationState,
    history,
    initialFilters,
    initialQuery,
    initialLastConnected,
    initialMetric,
    initialNetworkType,
    initialVersion,
    intitialTag,
    location.hash,
    location.pathname,
    location.search,
    setFilters,
    setSearch,
  ]);

  const devicesObject = useAppSelector(deviceWithStatusSelector);
  const devices: ReadonlyArray<Device> = getAllDevicesAsArray({
    byId: devicesObject as unknown as Record<string, Device>,
  });
  const devicesLastSeen = useAppSelector(devicesLastSeenSelector);
  const networksById = useAppSelector(networksSelector);
  const upgradablePlayerVersions = useAppSelector(
    upgradablePlayerVersionsSelector,
  );
  const strategies = useAppSelector(strategiesSelector);
  const rowsWithStrategyName = useMemo(
    () =>
      devices.map((device) => ({
        ...device,
        last_seen: {
          isHibernating: device.status?.hibernating ?? false,
          neverConnected: !device.has_connected,
          value: devicesLastSeen[device.id]?.last_seen ?? null,
        },
        strategy_name: strategies[device.strategy_id]?.name || "",
      })),
    [devices, devicesLastSeen, strategies],
  );
  const user = useAppSelector(getAuthUser);
  const userIsReadonly = isReadonly(user);
  const userIsAdmin = isAdmin(user);
  const [selectedItems, setSelectedItems] = useState<ReadonlyArray<Device>>([]);
  const [contextSelectedItem, setContextSelectedItem] = useState<Device | null>(
    null,
  );
  const [activeBulkAction, setActiveBulkAction] = useState<BulkAction | null>(
    null,
  );
  const selectionHasDeviceCount = useMemo<SelectionHasDeviceCount>(
    () =>
      selectedItems.reduce<SelectionHasDeviceCount>(
        (counts, device) => ({
          agent: counts.agent + (device.is_agent ? 1 : 0),
          cancelPending:
            counts.cancelPending + (hasPendingUpdate(device) ? 1 : 0),
          firmwareUpdate:
            counts.firmwareUpdate + (device.needs_firmware_upgrade ? 1 : 0),
          player:
            counts.player +
            (!device.is_agent &&
            (isSupportedUpdateOs(device.os_platform) || isKmpPlayer(device))
              ? 1
              : 0),
        }),
        { agent: 0, cancelPending: 0, firmwareUpdate: 0, player: 0 },
      ),
    [selectedItems],
  );

  const filteredRows = useMemo(
    () =>
      filterDevices({
        devices: rowsWithStrategyName,
        filters,
        isStrategiesEnabled,
        networksById,
        upgradablePlayerVersions,
      }),
    [
      filters,
      isStrategiesEnabled,
      networksById,
      rowsWithStrategyName,
      upgradablePlayerVersions,
    ],
  );

  const visibleRows = useMemo(
    () => filteredRows.filter((device) => filter(search, device)),
    [filteredRows, search],
  );

  const visibleDeviceStatsCounts = useMemo(
    () =>
      storesLoaded
        ? calculateVisibleLastSeenDeviceCounts(
            visibleRows,
            devicesLastSeen,
            networksById,
          )
        : deviceStatsCounts,
    [
      deviceStatsCounts,
      devicesLastSeen,
      networksById,
      storesLoaded,
      visibleRows,
    ],
  );

  const visibleDeviceMetricsCounts = useMemo(
    () =>
      storesLoaded
        ? calculateVisibleDeviceMetricsCounts(
            visibleRows,
            upgradablePlayerVersions,
          )
        : deviceMetricsCounts,
    [deviceMetricsCounts, storesLoaded, upgradablePlayerVersions, visibleRows],
  );

  const selectedTagOptions = useMemo(
    () => makeSelectedTagOptions(filters.tags, tagsOptions),
    [filters.tags, tagsOptions],
  );

  const handleLastSeenTierClick = useCallback(
    (tier: LastConnectedTier, networkType?: DeviceNetworkTypeFilter) => {
      setFilters((prev) => ({
        ...prev,
        lastConnected:
          tier === "Connected" ? ["Active", "Hibernating"] : [tier],
        networkType: [networkType ?? "Production"],
        visibleFilters: Array.from(
          new Set([
            ...getVisibleDeviceFilters(prev),
            "lastConnected" as const,
            "networkType" as const,
          ]),
        ),
      }));
    },
    [setFilters],
  );

  const handleNeverConnectedClick = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      lastConnected: ["Never Connected"],
      visibleFilters: Array.from(
        new Set([...getVisibleDeviceFilters(prev), "lastConnected" as const]),
      ),
    }));
  }, [setFilters]);

  const handleDeviceMetricClick = useCallback(
    (metric: DeviceMetricFilter) => {
      setFilters((prev) => ({
        ...prev,
        metric: [metric],
        visibleFilters: Array.from(
          new Set([...getVisibleDeviceFilters(prev), "metric" as const]),
        ),
      }));
    },
    [setFilters],
  );

  const clearFilters = useCallback(() => {
    setFilters((prev) => ({
      ...defaultFilters,
      visibleFilters: getVisibleDeviceFilters(prev),
    }));
  }, [defaultFilters, setFilters]);

  const availableNetworkOptions = useMemo(
    () => getAvailableNetworkOptions(rowsWithStrategyName, networkOptions),
    [networkOptions, rowsWithStrategyName],
  );

  const selectedNetworkOptions = useMemo(
    () => getSelectedFilterOptions(filters.network, availableNetworkOptions),
    [availableNetworkOptions, filters.network],
  );
  const selectedVersionOptions = useMemo(
    () => getSelectedFilterOptions(filters.version, versionOptions),
    [filters.version, versionOptions],
  );
  const availableOsOptions = useMemo(
    () => getAvailableOsOptions(rowsWithStrategyName),
    [rowsWithStrategyName],
  );
  const selectedOsOptions = useMemo(
    () => getSelectedFilterOptions(filters.os, availableOsOptions),
    [availableOsOptions, filters.os],
  );
  const availableFilters = useMemo(
    () => getAvailableDeviceFilters(networkFilter, isStrategiesEnabled),
    [isStrategiesEnabled, networkFilter],
  );
  const visibleFilters = useMemo(
    () =>
      getAvailableVisibleDeviceFilters(
        filters,
        networkFilter,
        isStrategiesEnabled,
      ),
    [filters, isStrategiesEnabled, networkFilter],
  );
  const savedFilterState = useMemo(() => {
    const { visibleFilters: _visibleFilters, ...appliedFilterValues } = filters;

    return {
      ...filters,
      visibleFilters: getAvailableVisibleDeviceFilters(
        appliedFilterValues,
        networkFilter,
        isStrategiesEnabled,
      ),
    };
  }, [filters, isStrategiesEnabled, networkFilter]);

  const handleVisibleFiltersChange = useCallback(
    (selectedFilterIds: ReadonlyArray<string>) => {
      const nextVisibleFilters = selectedFilterIds.filter(
        (id): id is DeviceFilterId =>
          availableFilters.some((option) => option.id === id),
      );

      setFilters((prev) => ({
        ...prev,
        lastConnected: nextVisibleFilters.includes("lastConnected")
          ? prev.lastConnected
          : [],
        contentSource: nextVisibleFilters.includes("contentSource")
          ? prev.contentSource
          : [],
        metric: nextVisibleFilters.includes("metric") ? prev.metric : [],
        network: nextVisibleFilters.includes("network") ? prev.network : [],
        networkType: nextVisibleFilters.includes("networkType")
          ? prev.networkType
          : [],
        os: nextVisibleFilters.includes("os") ? prev.os : [],
        tags: nextVisibleFilters.includes("tags") ? prev.tags : [],
        version: nextVisibleFilters.includes("version") ? prev.version : [],
        visibleFilters: nextVisibleFilters,
      }));
    },
    [availableFilters, setFilters],
  );

  const appliedFilters = useMemo<ReadonlyArray<AppliedFilter>>(() => {
    const lastConnectedFilters = getLastConnectedFilters(filters.lastConnected);
    const metricFilters = getDeviceMetricFilters(filters.metric);
    const appliedLastConnectedFilters =
      getAppliedLastConnectedFilters(lastConnectedFilters);

    return [
      ...appliedLastConnectedFilters.map((lastConnected) => ({
        key: `last-connected-${lastConnected}`,
        label: "Connectivity",
        onDelete: () =>
          setFilters((prev) => ({
            ...prev,
            lastConnected:
              lastConnected === "Connected"
                ? getLastConnectedFilters(prev.lastConnected).filter(
                    (filter) =>
                      filter !== "Connected" &&
                      filter !== "Active" &&
                      filter !== "Hibernating",
                  )
                : removeValue(
                    getLastConnectedFilters(prev.lastConnected),
                    lastConnected,
                  ),
          })),
        value: lastConnected,
      })),
      ...(networkFilter
        ? selectedNetworkOptions.map((networkOption) => ({
            key: `network-${asIdStr(networkOption.value)}`,
            label: "Network",
            onDelete: () =>
              setFilters((prev) => ({
                ...prev,
                network: removeValue(
                  getSelectedFilterValues(prev.network),
                  asIdStr(networkOption.value),
                ),
              })),
            value: networkOption.label,
          }))
        : []),
      ...selectedVersionOptions.map((versionOption) => ({
        key: `version-${asIdStr(versionOption.value)}`,
        label: "Version",
        onDelete: () =>
          setFilters((prev) => ({
            ...prev,
            version: removeValue(
              getSelectedFilterValues(prev.version),
              asIdStr(versionOption.value),
            ),
          })),
        value: versionOption.label,
      })),
      ...selectedTagOptions.map((tag) => ({
        key: `tag-${tag.value}`,
        label: "Tags",
        onDelete: () =>
          setFilters((prev) => ({
            ...prev,
            tags: removeValue(prev.tags ?? [], String(tag.value)),
          })),
        value: tag.label,
      })),
      ...selectedOsOptions.map((osOption) => ({
        key: `os-${asIdStr(osOption.value)}`,
        label: "OS",
        onDelete: () =>
          setFilters((prev) => ({
            ...prev,
            os: removeValue(prev.os ?? [], asIdStr(osOption.value)),
          })),
        value: osOption.label,
      })),
      ...metricFilters.map((metric) => ({
        key: `metric-${metric.toLowerCase().replace(/\s+/g, "-")}`,
        label: "Version Status",
        onDelete: () =>
          setFilters((prev) => ({
            ...prev,
            metric: removeValue(getDeviceMetricFilters(prev.metric), metric),
          })),
        value: metric,
      })),
      ...(!isStrategiesEnabled
        ? (filters.contentSource ?? []).map((contentSource) => ({
            key: `content-source-${contentSource
              .toLowerCase()
              .replace(/\s+/g, "-")}`,
            label: "Content Source",
            onDelete: () =>
              setFilters((prev) => ({
                ...prev,
                contentSource: removeValue(
                  prev.contentSource ?? [],
                  contentSource,
                ),
              })),
            value: contentSource,
          }))
        : []),
      ...(filters.networkType ?? []).map((networkType) => ({
        key: `network-type-${networkType.toLowerCase()}`,
        label: "Network Type",
        onDelete: () =>
          setFilters((prev) => ({
            ...prev,
            networkType: removeValue(prev.networkType ?? [], networkType),
          })),
        value: networkType,
      })),
    ];
  }, [
    filters.contentSource,
    filters.lastConnected,
    filters.metric,
    filters.network,
    filters.networkType,
    filters.os,
    filters.tags,
    filters.version,
    isStrategiesEnabled,
    networkFilter,
    selectedNetworkOptions,
    selectedOsOptions,
    selectedTagOptions,
    selectedVersionOptions,
    setFilters,
  ]);

  const filterOptions = useMemo(
    () => (
      <DeviceTableFilters
        networkFilter={networkFilter}
        networkOptions={availableNetworkOptions}
        onChange={setFilters}
        osOptions={availableOsOptions}
        selectedTagOptions={selectedTagOptions}
        tagsOptions={tagsOptions}
        value={filters}
        versionOptions={versionOptions}
        visibleFilters={visibleFilters}
      />
    ),
    [
      availableNetworkOptions,
      availableOsOptions,
      filters,
      networkFilter,
      selectedTagOptions,
      tagsOptions,
      versionOptions,
      visibleFilters,
    ],
  );

  const setActionSelectedItems = useCallback(
    (items: ReadonlyArray<Device>) => {
      if (contextSelectedItem) {
        setContextSelectedItem(items[0] ?? null);
      } else {
        setSelectedItems(items);
      }
    },
    [contextSelectedItem],
  );

  const actionSelectedItems = useMemo<ReadonlyArray<Device>>(
    () => (contextSelectedItem ? [contextSelectedItem] : selectedItems),
    [contextSelectedItem, selectedItems],
  );

  const openBulkAction = useCallback(
    (action: BulkAction, item?: Device) => {
      if (item) {
        setContextSelectedItem(item);
        setActiveBulkAction(action);
        return;
      }
      if (
        selectedItems.length === 0 &&
        action !== BULK_ACTIONS.REGISTER_SINGLE &&
        action !== BULK_ACTIONS.REGISTER_BULK
      ) {
        return;
      }
      setActiveBulkAction(action);
    },
    [selectedItems],
  );

  const closeBulkAction = () => {
    setActiveBulkAction(null);
    setContextSelectedItem(null);
  };

  const handleExportSelectedDevices = useCallback(() => {
    const selectedIds = new Set(selectedItems.map((device) => device.id));
    exportSelectedDevices({
      devices: rowsWithStrategyName.filter((device) =>
        selectedIds.has(device.id),
      ),
      isStrategiesEnabled,
      storageKey,
      upgradablePlayerVersions,
    });
  }, [
    isStrategiesEnabled,
    rowsWithStrategyName,
    selectedItems,
    storageKey,
    upgradablePlayerVersions,
  ]);

  const bulkActionBarItems = useMemo<ReadonlyArray<BulkActionBarItem>>(() => {
    const exportAction = {
      label: "Export",
      onClick: handleExportSelectedDevices,
    };

    if (userIsReadonly) {
      return [exportAction];
    }

    return [
      exportAction,
      {
        label: "Restart",
        onClick: () => openBulkAction(BULK_ACTIONS.RESTART),
      },

      ...(!isStrategiesEnabled && userIsAdmin
        ? [
            {
              label: "Edit Content Source",
              onClick: () => openBulkAction(BULK_ACTIONS.STRATEGY),
            },
          ]
        : []),

      ...(selectionHasDeviceCount.player > 0
        ? [
            {
              label: `Up/Downgrade Player (${selectionHasDeviceCount.player})`,
              onClick: () => openBulkAction(BULK_ACTIONS.UP_DOWNGRADE_PLAYER),
            },
          ]
        : []),

      ...(selectionHasDeviceCount.cancelPending > 0
        ? [
            {
              label: `Cancel Pending (${selectionHasDeviceCount.cancelPending})`,
              onClick: () =>
                openBulkAction(BULK_ACTIONS.CANCEL_PENDING_UPGRADE),
            },
          ]
        : []),

      ...(selectionHasDeviceCount.agent > 0
        ? [
            {
              label: `Upgrade Agent (${selectionHasDeviceCount.agent})`,
              onClick: () => openBulkAction(BULK_ACTIONS.UPGRADE_AGENT),
            },
          ]
        : []),

      ...(selectionHasDeviceCount.firmwareUpdate > 0
        ? [
            {
              label: `Upgrade Firmware (${selectionHasDeviceCount.firmwareUpdate})`,
              onClick: () => openBulkAction(BULK_ACTIONS.UPGRADE_FIRMWARE),
            },
          ]
        : []),
      {
        color: "error",
        label: "Delete",
        moreActionItem: true,
        onClick: () => openBulkAction(BULK_ACTIONS.DELETE),
      },
    ];
  }, [
    handleExportSelectedDevices,
    isStrategiesEnabled,
    openBulkAction,
    selectionHasDeviceCount,
    userIsAdmin,
    userIsReadonly,
  ]);

  const addButton = !userIsReadonly ? (
    <ButtonGroup
      split={{
        primary: {
          label: "Add Device",
          onClick: () => openBulkAction(BULK_ACTIONS.REGISTER_SINGLE),
        },
        items: [
          {
            label: "Add Device",
            onClick: () => openBulkAction(BULK_ACTIONS.REGISTER_SINGLE),
          },
          {
            label: "Bulk Add",
            onClick: () => openBulkAction(BULK_ACTIONS.REGISTER_BULK),
          },
        ],
      }}
    />
  ) : null;

  const ActiveBulkActionComponent =
    activeBulkAction != null ? BULK_ACTION_COMPONENTS[activeBulkAction] : null;

  const getContextMenuItems = useCallback(
    (row: Device): ReadonlyArray<ContextMenuItem> => {
      const deviceCancelPending = hasPendingUpdate(row);
      const deviceId = row.device_id;
      const deviceIsAgent = row.is_agent;
      const devicePlayerUpgrade =
        !row.is_agent &&
        (isSupportedUpdateOs(row.os_platform) || isKmpPlayer(row));
      const deviceNeedsFirmwareUpdate = row.needs_firmware_upgrade;
      return [
        {
          label: "Edit",
          onClick: () => history.push(`/devices/${deviceId}/edit`),
        },
        {
          label: "Restart",
          onClick: () => openBulkAction(BULK_ACTIONS.RESTART, row),
        },
        ...(devicePlayerUpgrade
          ? [
              {
                label: "Up/Downgrade Player",
                onClick: () =>
                  openBulkAction(BULK_ACTIONS.UP_DOWNGRADE_PLAYER, row),
              },
            ]
          : []),
        ...(deviceIsAgent
          ? [
              {
                label: "Upgrade Agent",
                onClick: () => openBulkAction(BULK_ACTIONS.UPGRADE_AGENT, row),
              },
            ]
          : []),
        ...(deviceNeedsFirmwareUpdate
          ? [
              {
                label: "Upgrade Firmware",
                onClick: () =>
                  openBulkAction(BULK_ACTIONS.UPGRADE_FIRMWARE, row),
              },
            ]
          : []),
        ...(deviceCancelPending
          ? [
              {
                label: "Cancel Pending Up/Downgrades",
                onClick: () =>
                  openBulkAction(BULK_ACTIONS.CANCEL_PENDING_UPGRADE, row),
              },
            ]
          : []),
        {
          color: "error",
          label: "Delete",
          onClick: () => openBulkAction(BULK_ACTIONS.DELETE, row),
        },
      ];
    },
    [history, openBulkAction],
  );

  const columns = useMemo(
    () =>
      getColumns({
        isStrategiesEnabled,
        getContextMenuItems,
      }),
    [isStrategiesEnabled, getContextMenuItems],
  );

  const hiddenColumns = useMemo(() => {
    const cols: string[] = [];
    if (!isStrategiesEnabled) cols.push("strategy_name");
    if (userIsReadonly) cols.push("actions");
    return cols;
  }, [isStrategiesEnabled, userIsReadonly]);

  const deviceTableCommonProps = {
    appliedFilters: {
      filters: appliedFilters,
      onClearAll: clearFilters,
    },
    availableFilters: {
      filters: availableFilters,
      onChange: handleVisibleFiltersChange,
      selectedFilterIds: visibleFilters,
    },
    bulkActionItems: bulkActionBarItems,
    columnOptions: {
      defaultVisible: DEVICE_TABLE_DEFAULT_VISIBLE_COLUMNS,
      locked: ["last_seen", "name", "actions"],
    },
    columns,
    dataTestId: {
      appliedFilters: "device-table-applied-filters",
      availableFilters: "device-table-available-filters",
      bulkActionBar: "device-table-bulk-action-bar",
      savedFilters: "device-table-saved-filters",
      settings: "device-table-column-selection",
    },
    filter,
    getRowProps: (row: Device) => ({
      hover: true,
      onClick: (e: MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        // ignore clicks that are not on the cell itself
        const isVeneerCell = target.closest(
          'th[data-component="veneer-table-cell"], td[data-component="veneer-table-cell"]',
        );
        if (!isVeneerCell) return;
        // ignore checkbox clicks
        if (target.closest("input[type='checkbox']")) {
          return;
        }

        if (
          target.closest(
            "a, button, [role='button'], [role='menuitem'], input, select, textarea, label",
          )
        ) {
          return;
        }

        onRowClick(row, true);
      },
    }),
    hideColumns: hiddenColumns,
    initialPageSize: INITIAL_PAGE_SIZE,
    initialSort: { id: "last_seen", desc: true },
    onSearchChange: setSearch,
    primaryActions: addButton,
    rows: filteredRows,
    savedFilters: {
      currentFilters: savedFilterState,
      onApply: setFilters,
    },
    searchText: search,
    settingsKey: storageKey,
    viewOptions: visibleFilters.length > 0 && filterOptions,
  };

  const deviceTableActions = useMemo(
    () => ({
      openAgentUpgradeForDevice: (device: Device) =>
        openBulkAction(BULK_ACTIONS.UPGRADE_AGENT, device),
      openBulkAction,
      openCancelPendingForDevice: (device: Device) =>
        openBulkAction(BULK_ACTIONS.CANCEL_PENDING_UPGRADE, device),
      openDeleteForDevice: (device: Device) =>
        openBulkAction(BULK_ACTIONS.DELETE, device),
      openFirmwareUpgradeForDevice: (device: Device) =>
        openBulkAction(BULK_ACTIONS.UPGRADE_FIRMWARE, device),
      openRestartForDevice: (device: Device) =>
        openBulkAction(BULK_ACTIONS.RESTART, device),
      openUpgradeForDevice: (device: Device) =>
        openBulkAction(BULK_ACTIONS.UP_DOWNGRADE_PLAYER, device),
    }),
    [openBulkAction],
  );

  return (
    <DeviceTableActionsProvider value={deviceTableActions}>
      <Stack gap={2}>
        <DeviceStats
          counts={visibleDeviceStatsCounts}
          deviceMetricsCounts={visibleDeviceMetricsCounts}
          onDeviceMetricClick={handleDeviceMetricClick}
          onLastSeenTierClick={handleLastSeenTierClick}
          onNeverConnectedClick={handleNeverConnectedClick}
        />
        <Panel p={0}>
          {storesLoaded ? (
            <DataTable
              {...deviceTableCommonProps}
              getIdentifier={(item: Device) => String(item.id)}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
            />
          ) : (
            <LoadingIndicator />
          )}
        </Panel>
        {ActiveBulkActionComponent && (
          <ActiveBulkActionComponent
            onCancel={closeBulkAction}
            open={Boolean(activeBulkAction)}
            selectedItems={actionSelectedItems}
            setSelectedItems={setActionSelectedItems}
          />
        )}
        {activeBulkAction === BULK_ACTIONS.REGISTER_SINGLE && (
          <RegisterSingleDeviceDrawer
            networkOptions={networkOptions}
            onCancel={closeBulkAction}
            open={Boolean(activeBulkAction)}
            tagsOptions={tagsOptions}
          />
        )}
        {activeBulkAction === BULK_ACTIONS.REGISTER_BULK && (
          <RegisterBulkDevicesDrawer
            networkOptions={networkOptions}
            onCancel={closeBulkAction}
            open={Boolean(activeBulkAction)}
          />
        )}
      </Stack>
    </DeviceTableActionsProvider>
  );
}
